import { useTranslation } from 'react-i18next';
import PageHeader from '@casops/components/layout/page-header/PageHeader';
import { ModelName, QueryTag, TagList } from '@casops/store/api';
import StandardPage from '@casops/templates/standard-page/StandardPage';
import { CollectionView } from '@casops/components/layout/collection-view/CollectionView';
import { useTableData } from '@casops/shared/hooks/useTableData';
import { Permission } from '@casops/permissions';
import CreateItemButton from '@casops/components/inputs/create-item-button/CreateItemButton';
import { SidePanel } from '@casops/store/slices/globalSlice';
import {
  AssetCertificateModel,
  useGetListCertificatesQuery,
} from '@casops/store/apis/assetCertificatesApi';
import useCertificatesColDefs from './useCertificatesColDefs';
import { TagDescription } from '@reduxjs/toolkit/query';
import { useCallback } from 'react';
import GridActionButton from '@casops/components/inputs/grid-action-button/GridActionButton';
import { downloadBlob } from '@casops/shared/utils/fileDownloads';

type Model = AssetCertificateModel;

const sliceKey = ModelName.Certificate;
const modelName = ModelName.Certificate;
const sidePanel = SidePanel.CertificateImport;
const permission = Permission.Admin;

const tagsToClearOnRefresh: TagDescription<QueryTag>[] = [
  { type: QueryTag.Certificate, id: TagList },
];

// The data structure for exported certificates to be consumed by DM Manager.
// The 'assetId' field contains the 'did' value of the AssetCertificateModel.
interface CertificatePayload {
  assetId: string;
  csr: string;
}

const handleExportCsrs = (certificates: Model[], selectedIds: number[]) => {
  if (certificates) {
    const certificatesPayload: CertificatePayload[] = certificates
      .filter(({ id }) => selectedIds.includes(id))
      .map(({ did, csr }) => ({ assetId: did, csr }));

    downloadBlob(
      JSON.stringify(certificatesPayload, null, 2),
      'cas-device-certificate-requests.json',
      'application/json;charset=utf-8;',
    );
  }
};

export function CertificatesPage() {
  const { t } = useTranslation();

  const columnDefs = useCertificatesColDefs();

  const noResultsDescription = t('Model.Collection.NoItems', { modelName });

  const { data, ...requestRemainder } = useGetListCertificatesQuery(undefined, {
    refetchOnMountOrArgChange: 60,
  });

  const { hasData, noDataDescription } = useTableData<Model>({
    data,
    ...requestRemainder,
    noResultsDescription,
  });

  const onClickExport = useCallback(
    (ids: number[]): void => {
      if (data) {
        handleExportCsrs(data, ids);
      }
    },
    [data],
  );

  return (
    <StandardPage
      header={
        <PageHeader
          title={t('Navigation.Certificates')}
          tagsToClearOnRefresh={tagsToClearOnRefresh}
        />
      }
    >
      <CollectionView<Model>
        sliceKey={sliceKey}
        modelName={modelName}
        columnDefs={columnDefs}
        rowData={data}
        hasData={hasData}
        noDataDescription={noDataDescription}
        rowSelectMode="multiRow"
        actions={
          <>
            <GridActionButton
              sliceKey={sliceKey}
              permission={permission}
              onClick={onClickExport}
              disabled={!data?.length}
              label={t('Export')}
            />
            <CreateItemButton
              modelName={modelName}
              sidePanel={sidePanel}
              permission={permission}
              label={t('Import')}
              icon="arrow-down"
            />
          </>
        }
      />
    </StandardPage>
  );
}

export default CertificatesPage;
